import { Box, Typography, Paper, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { formatDateTime } from '../../utils/utils';

function JobDetailsHeader({ job }) {
	const getStatusDetail = () => {
		const isIngestedWithCount =
			job.status === 'ingested' &&
			Object.prototype.hasOwnProperty.call(job.statistics, 'emails_ingested');

		const isSyncedWithCount =
			job.status === 'synced' &&
			Object.prototype.hasOwnProperty.call(
				job.statistics,
				'interaction_messages_synced'
			);

		if (isIngestedWithCount) {
			const emailCount = job.statistics.emails_ingested.toLocaleString();
			return `${emailCount} emails pulled`;
		} else if (isSyncedWithCount) {
			const messageCount =
				job.statistics.interaction_messages_synced.toLocaleString();
			return `${messageCount} emails synced to CRM`;
		} else {
			return job.status_detail ? JSON.stringify(job.status_detail) : null;
		}
	};

	const detailItems = [
		{
			label: 'PROVIDER',
			value: job.provider, //microsoft or google
			capitalize: true,
			testId: 'provider-section',
		},
		{
			label: 'TYPE',
			value: job.trigger_type === 'manual' ? 'Manual' : 'Automatic',
			testId: 'type-section',
		},
		{
			label: 'RANGE START',
			value: formatDateTime(job.range_start),
			testId: 'range-start-section',
		},
		{
			label: 'RANGE END',
			value: formatDateTime(job.range_end),
			testId: 'range-end-section',
		},
		{
			label: 'CREATED AT',
			value: formatDateTime(job.created_at),
			testId: 'created-at-section',
		},
		{
			label: 'STATUS DETAIL',
			value: getStatusDetail(),
			isStatusDetail: true,
			testId: 'status-detail-section',
		},
	];

	// Labels: PROVIDER | TYPE | RANGE START | RANGE END | CREATED AT | STATUS DETAIL
	const Label = ({ label }) => (
		<Typography
			variant='caption'
			sx={{
				color: '#9e9e9e',
				fontWeight: 600,
				fontSize: '11px',
				letterSpacing: '0.5px',
				mb: 0.25,
			}}
		>
			{label}
		</Typography>
	);

	// Values: Microsoft/Gmail | Manual/Automatic | Datetime | Datetime | Datetime | ## emails pulled / ## emails synced to CRM
	const Value = ({ fontWeight, item }) => (
		<Typography
			variant='body2'
			sx={{
				textTransform: item.capitalize ? 'capitalize' : 'none',
				fontSize: '13px',
				fontWeight,
				letterSpacing: '0.3px',
				overflow: 'hidden',
				textOverflow: 'ellipsis',
				whiteSpace: 'nowrap',
			}}
		>
			{item.value || '-'}
		</Typography>
	);

	// Wrapped by tooltip to display full content of long status detail
	const StatusDetail = ({ item }) => (
		<Tooltip title={item.value} placement='top'>
			<Typography
				variant='body2'
				sx={{
					textTransform: item.capitalize ? 'capitalize' : 'none',
					fontSize: '13px',
					fontWeight: 600,
					letterSpacing: '0.3px',
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap',
				}}
			>
				{item.value || '-'}
			</Typography>
		</Tooltip>
	);

	return (
		<Paper
			elevation={0}
			sx={(theme) => ({
				display: 'grid',
				gridTemplateColumns: 'repeat(6, 1fr)',
				gap: 2,
				px: 3,
				py: 2,
				mb: 2,
				borderRadius: 1,
				backgroundColor: theme.palette.background.lightBlue,
        border: `1px solid ${theme.palette.divider}`
			})}
		>
			{detailItems.map((item, index) => (
				<Box
					key={index}
					data-testid={item.testId}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						borderRight:
							index < detailItems.length - 1 ? '1.5px solid #e8e8e8' : 'none',
						pr: index < detailItems.length - 1 ? 2 : 0,
						minWidth: 0,
						flex: 1,
					}}
				>
					<Label label={item.label} />

					{item.isStatusDetail && item.value && item.value !== '' ? (
						<StatusDetail item={item} />
					) : (
						<Value fontWeight={600} item={item} />
					)}
				</Box>
			))}
		</Paper>
	);
}

JobDetailsHeader.propTypes = {
	job: PropTypes.object.isRequired,
};

export default JobDetailsHeader;
